import React, { useState, useEffect, useRef } from 'react';
import { Mail, Phone, MapPin, Send, Linkedin, Twitter, Github, Instagram, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Contact() {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.dataset.animate]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observerRef.current.observe(section));

    return () => observerRef.current.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Simulate form submission
    setFormStatus('sending');
    
    setTimeout(() => {
      setFormStatus('success');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      setTimeout(() => {
        setFormStatus(null);
      }, 5000);
    }, 1500);
  };

  return (
    <div className="bg-white text-zinc-900">
      {/* Back to Home Button */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-zinc-700 hover:text-zinc-900 transition-colors duration-300 hover:gap-3"
          >
            <Home size={18} />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      {/* Header Section */}
      <section className="border-b border-zinc-200" data-animate="header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className={`max-w-3xl transition-all duration-1000 ease-out ${
            visibleSections.has('header') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="inline-block px-4 py-1 bg-zinc-900 text-white text-xs font-medium tracking-wider mb-8">
              CONTACT US
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold tracking-tight mb-8 leading-[1.1]">
              Get in Touch
            </h1>
            <p className="text-xl text-zinc-600 leading-relaxed">
              Have a project in mind? Want to discuss how we can help your business grow? We'd love to hear from you. Drop us a message and we'll get back to you within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content: Form + Contact Info */}
      <section className="border-b border-zinc-200" data-animate="main">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className={`lg:col-span-2 transition-all duration-1000 ease-out ${
              visibleSections.has('main') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}>
              <div className="bg-zinc-50 border border-zinc-200 p-8 lg:p-12">
                <h2 className="text-3xl font-bold mb-8">Send us a message</h2>
                
                <div className="space-y-6">
                  {/* Name Field */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-zinc-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-zinc-200 focus:border-zinc-900 focus:outline-none transition-colors duration-300 bg-white"
                      placeholder="John Doe"
                    />
                  </div>

                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-zinc-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-zinc-200 focus:border-zinc-900 focus:outline-none transition-colors duration-300 bg-white"
                      placeholder="john@example.com"
                    />
                  </div>

                  {/* Subject Field */}
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-zinc-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-zinc-200 focus:border-zinc-900 focus:outline-none transition-colors duration-300 bg-white"
                      placeholder="Project Inquiry"
                    />
                  </div>

                  {/* Message Field */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-zinc-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={6}
                      className="w-full px-4 py-3 border-2 border-zinc-200 focus:border-zinc-900 focus:outline-none transition-colors duration-300 resize-none bg-white"
                      placeholder="Tell us about your project..."
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    onClick={handleSubmit}
                    disabled={formStatus === 'sending'}
                    className="group bg-zinc-900 text-white px-8 py-4 text-base font-medium transition-all duration-300 inline-flex items-center gap-3 hover:px-10 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center sm:justify-start"
                  >
                    {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
                    <Send size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>

                  {/* Success Message */}
                  {formStatus === 'success' && (
                    <div className="bg-zinc-900 text-white px-6 py-4 border-l-4 border-white">
                      <p className="font-medium">Message sent successfully!</p>
                      <p className="text-sm text-zinc-300 mt-1">We'll get back to you within 24 hours.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className={`space-y-8 transition-all duration-1000 ease-out delay-200 ${
              visibleSections.has('main') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}>
              <div>
                <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
                <p className="text-zinc-600 leading-relaxed mb-8">
                  Prefer to reach out directly? Here's how you can get in touch with us.
                </p>
              </div>

              {/* Address */}
              <div className="border-l-4 border-zinc-900 pl-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-zinc-900 text-white flex items-center justify-center flex-shrink-0 mt-1">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">Visit Us</h4>
                    <p className="text-zinc-600 leading-relaxed">
                      123 Innovation Drive<br />
                      Tech District, Floor 5<br />
                      San Francisco, CA 94107<br />
                      United States
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="border-l-4 border-zinc-900 pl-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-zinc-900 text-white flex items-center justify-center flex-shrink-0 mt-1">
                    <Phone size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">Call Us</h4>
                    <a 
                      href="tel:+14155551234" 
                      className="text-zinc-600 hover:text-zinc-900 transition-colors duration-300"
                    >
                      +1 (415) 555-1234
                    </a>
                    <p className="text-sm text-zinc-500 mt-1">Mon-Fri, 9am-6pm PST</p>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="border-l-4 border-zinc-900 pl-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-zinc-900 text-white flex items-center justify-center flex-shrink-0 mt-1">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">Email Us</h4>
                    <a 
                      href="mailto:hello@digitaltrent.com" 
                      className="text-zinc-600 hover:text-zinc-900 transition-colors duration-300 break-all"
                    >
                      hello@digitaltrent.com
                    </a>
                    <p className="text-sm text-zinc-500 mt-1">We reply within 24 hours</p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="pt-8 border-t border-zinc-200">
                <h4 className="font-bold text-lg mb-4">Follow Us</h4>
                <div className="flex gap-3">
                  <a
                    href="#"
                    className="w-12 h-12 border-2 border-zinc-900 flex items-center justify-center transition-all duration-300 hover:bg-zinc-900 hover:text-white"
                    aria-label="LinkedIn"
                  >
                    <Linkedin size={20} />
                  </a>
                  <a
                    href="#"
                    className="w-12 h-12 border-2 border-zinc-900 flex items-center justify-center transition-all duration-300 hover:bg-zinc-900 hover:text-white"
                    aria-label="Twitter"
                  >
                    <Twitter size={20} />
                  </a>
                  <a
                    href="#"
                    className="w-12 h-12 border-2 border-zinc-900 flex items-center justify-center transition-all duration-300 hover:bg-zinc-900 hover:text-white"
                    aria-label="Github"
                  >
                    <Github size={20} />
                  </a>
                  <a
                    href="#"
                    className="w-12 h-12 border-2 border-zinc-900 flex items-center justify-center transition-all duration-300 hover:bg-zinc-900 hover:text-white"
                    aria-label="Instagram"
                  >
                    <Instagram size={20} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Office Hours / Additional Info */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-animate="info">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <div className={`grid md:grid-cols-3 gap-8 transition-all duration-1000 ease-out ${
            visibleSections.has('info') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <div className="bg-white border border-zinc-200 p-8">
              <h3 className="text-xl font-bold mb-3">Office Hours</h3>
              <div className="space-y-2 text-zinc-600">
                <div className="flex justify-between">
                  <span>Monday - Friday:</span>
                  <span className="font-medium">9am - 6pm</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday:</span>
                  <span className="font-medium">10am - 4pm</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday:</span>
                  <span className="font-medium">Closed</span>
                </div>
              </div>
            </div>

            <div className="bg-zinc-900 text-white p-8">
              <h3 className="text-xl font-bold mb-3">Response Time</h3>
              <p className="text-zinc-300 leading-relaxed">
                We typically respond to all inquiries within 24 hours during business days. Urgent matters are prioritized.
              </p>
            </div>

            <div className="bg-white border border-zinc-200 p-8">
              <h3 className="text-xl font-bold mb-3">Career Inquiries</h3>
              <p className="text-zinc-600 leading-relaxed mb-4">
                Interested in joining our team?
              </p>
              <a 
                href="#" 
                className="text-zinc-900 font-medium hover:underline inline-flex items-center gap-2 transition-all duration-300"
              >
                View Open Positions
                <Send size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section - Placeholder */}
      <section data-animate="map">
        <div className={`transition-all duration-1000 ease-out ${
          visibleSections.has('map') ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="bg-zinc-100 border-t border-b border-zinc-200 h-96 flex items-center justify-center">
            <div className="text-center">
              <MapPin size={48} className="mx-auto mb-4 text-zinc-400" />
              <p className="text-zinc-600 font-medium">Interactive Map</p>
              <p className="text-sm text-zinc-500 mt-2">123 Innovation Drive, San Francisco, CA 94107</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ or Additional CTA */}
      <section data-animate="cta">
        <div className={`max-w-4xl mx-auto px-6 lg:px-8 py-24 lg:py-32 text-center transition-all duration-1000 ease-out ${
          visibleSections.has('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <h2 className="text-3xl lg:text-5xl font-bold mb-6 leading-tight">
            Not sure where to start?
          </h2>
          <p className="text-lg text-zinc-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Schedule a free 30-minute consultation with our team. We'll discuss your goals, challenges, and how we can help you achieve success.
          </p>
          <button className="group border-2 border-zinc-900 text-zinc-900 px-10 py-5 text-base font-medium transition-all duration-300 hover:bg-zinc-900 hover:text-white inline-flex items-center gap-3">
            Schedule a Call
            <Phone size={18} className="transition-transform duration-300 group-hover:rotate-12" />
          </button>
        </div>
      </section>
    </div>
  );
}